import { useEffect, useRef, useMemo, memo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation, useHistory } from 'react-router-dom'
import { Box } from '@material-ui/core'
import { useUserProfile } from 'commons/useUserProfile'

import {
  mappedFailedSubscribers,
  setActiveStep,
  setCancelImportDialog,
  setIsSocketConnected,
  setShowProgressBar as setShowProgressBarOnCSVImport,
  setValidationInfo,
  useImportCSVData,
} from 'app/store/importCSV'
import {
  setSchoolImportProcessStatus,
  useImportExternalSourceData,
  setShowProgressBar as setShowProgressBarOnSISImport,
} from 'app/store/importExternalSource'
import {
  IMPORT_EXTERNAL_SOURCE_TAB,
  STEP_PROGRESS_THREE,
  STEP_PROGRESS_TWO,
} from 'app/main/subscribersV4/AddSubscibers/constants'
import {
  cancelImportThunk,
  clearCSVData,
  loadCSVData,
} from 'app/store/importCSV/importCSVExtraReducers'
import { checkSISDataImportThunk } from 'app/store/importExternalSource/importExternalSourceReducers'
import { Routes } from 'app/services/routeService/routeService'
import { usePlace } from 'commons/usePlace'
import { useAccessToken } from 'app/auth/authHooks'
import {
  IMPORT_PROCESS_TYPE,
  VALIDATE_PROCESS_TYPE,
  SocketEvent,
  SCHOOL_IMPORT_STATUS_COMPLETED,
  SCHOOL_IMPORT_STATUS_STARTED,
  CANCEL_IMPORT_CSV,
  CANCEL_SIS_IMPORT,
  SCHOOL_IMPORT_STATUS_CANSEL,
} from '../constansts'
import themesConfig from 'app/fuse-configs/themesConfig'
import MessageAlert from 'app/components/MessageAlert'
import CancelImportDialog from '../PreviewDataTable/dialogs/CancelImportDialog'
import { connectedSocket } from '../connectedSocket'
import ProgressBar from './ProgressBar'

import useStyles from './styles'
import effects from 'app/effects/effects'
import { setCSVFileData } from 'app/store/importCSV'
import { updateFailedSubscribers } from 'app/store/importCSV/reducers'
import { useFlag } from '@unleash/proxy-client-react'

const BackgroundProgressBarImportProcess = () => {
  const socketRef = useRef(null)
  const { formatMessage } = useIntl()

  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const history = useHistory()
  const importCSVData = useImportCSVData()
  const {
    data: { fileName, fileSize, validationInfo },
    activeStep,
    isSocketConnected,
    showProgressBar: showProgressBarOnCSVImport,
  } = importCSVData

  const classes = useStyles()

  const {
    isListenSocketSchoolImport,
    schoolImportProcessStatusData,
    checkImportData,
    showProgressBar: showProgressBarOnSISImport,
  } = useImportExternalSourceData()
  const { importStatus: SISImportStatus } = checkImportData
  const { status } = schoolImportProcessStatusData

  const place = usePlace()
  const { id: placeId } = place
  const accessToken = useAccessToken()

  const { percent, validatedChunkCount, chunkCount, type } = validationInfo

  const userProfile = useUserProfile()
  const updateProgress = useCallback(
    async function () {
      if (!accessToken) return
      const details = await effects.apis.importCSV.getSubscribersImportDetails()
      if (!details) {
        await clearCSVData()
        return
      }

      if (!details.isInProcess && !details.showDialog) {
        await clearCSVData()
        return
      }

      const { errors } =
        await effects.apis.importCSV.getSubscribersImportErrors({
          processId: details.id,
        })

      if (details.userId !== userProfile.id) return

      const csvData = await loadCSVData()
      if (csvData) {
        let showProgressBar = null
        showProgressBar =
          details.percent !== undefined && details.percent !== 100

        dispatch(
          setCSVFileData({
            data: {
              ...csvData,
              validationInfo: {
                ...csvData.validationInfo,
                type: details.type,
                importId: details.id,
                percent: details.percent ?? csvData.validationInfo.percent,
                failedSubscribers: errors
                  ? updateFailedSubscribers(
                      details.type,
                      csvData.importedSubscribersIds,
                      errors,
                      [],
                    )
                  : [],
              },
            },
            activeStep:
              details.type === 'validate' && showProgressBar
                ? STEP_PROGRESS_TWO
                : STEP_PROGRESS_THREE,
            showProgressBar,
          }),
        )
        dispatch(mappedFailedSubscribers())
      }
      dispatch(setIsSocketConnected(true))
    },
    [userProfile],
  )

  useEffect(() => {
    updateProgress()
  }, [updateProgress])

  const cancelUploadOrImport = useCallback((cancelImportType) => {
    dispatch(setCancelImportDialog(cancelImportType))
  }, [])

  const showSnackbar = (msg) => {
    const secondsInmillisec = 10000
    dispatch(
      MessageAlert.showSuccess({ message: msg, duration: secondsInmillisec }),
    )
  }

  const onImportProcessPercentage = ({
    percent,
    type,
    failedSubscribers,
    chunkCount,
    importId,
  }) => {
    dispatch(
      setValidationInfo({
        percent,
        type,
        failedSubscribers,
        chunkCount,
        importId,
      }),
    )

    if (percent >= 100 && validationInfo.percent < 100) {
      updateProgress()
    }
  }

  const onSubscribersDeletionStatus = () => {
    showSnackbar(
      formatMessage({ id: 'subscribers.import.importCanceled.message' }),
    )
  }

  const onSchoolImportProcess = (data) => {
    const { status } = data
    const errorMessage = formatMessage({ id: 'wrong.msg' })
    if (status === SCHOOL_IMPORT_STATUS_COMPLETED) {
      dispatch(checkSISDataImportThunk({ errorMessage }))
    }
    dispatch(setSchoolImportProcessStatus(data))
  }

  useEffect(() => {
    const socket = socketRef.current
    if (socket) {
      if (isSocketConnected) {
        socket?.on(
          SocketEvent.IMPORT_PROCESS_PERCENTAGE,
          onImportProcessPercentage,
        )
        socket?.on(
          SocketEvent.SUBSCRIBERS_DELETION_STATUS,
          onSubscribersDeletionStatus,
        )
      } else {
        socket?.off(SocketEvent.IMPORT_PROCESS_PERCENTAGE)
        socket?.off(SocketEvent.SUBSCRIBERS_DELETION_STATUS)
      }
      if (isListenSocketSchoolImport) {
        socket?.on(
          SocketEvent.SUBSCRIBERS_SCHOOL_IMPORT_STATUS,
          onSchoolImportProcess,
        )
      } else {
        socket?.off(SocketEvent.SUBSCRIBERS_SCHOOL_IMPORT_STATUS)
      }
    }
  }, [isSocketConnected, isListenSocketSchoolImport])

  useEffect(() => {
    if (placeId && accessToken) {
      const socket = connectedSocket(placeId, accessToken)

      if (!socketRef.current) {
        socketRef.current = socket
      }
    }

    return () => {
      socketRef.current?.disconnect()
    }
  }, [accessToken, placeId])

  useEffect(() => {
    if (
      (type === VALIDATE_PROCESS_TYPE || type === IMPORT_PROCESS_TYPE) &&
      percent === 100
    ) {
      dispatch(mappedFailedSubscribers())
      setTimeout(() => {
        dispatch(setActiveStep(STEP_PROGRESS_THREE))
        dispatch(setShowProgressBarOnCSVImport(false))
      }, 300)
    }
  }, [percent])

  const goToImportSubscriberPage = useCallback(() => {
    dispatch(setShowProgressBarOnSISImport(false))
    history.push({
      pathname: Routes.importSubscriberV4(),
      state: { tab: IMPORT_EXTERNAL_SOURCE_TAB },
    })
  }, [])

  const activeBackgroundProgressBars = useMemo(() => {
    const isCompletedSISImport = status === SCHOOL_IMPORT_STATUS_COMPLETED
    const isStartedSISImport = status === SCHOOL_IMPORT_STATUS_STARTED
    const isCancellationProcess =
      SISImportStatus === SCHOOL_IMPORT_STATUS_CANSEL
    const allowedPaths = [Routes.importSubscriberV4(), Routes.maintenance()]
    const isPathnameAllowed = allowedPaths.includes(pathname)

    const createProgressBar = ({
      id,
      showCondition,
      title,
      percent,
      variant,
      color,
      importInfo,
      cancelImportType,
      handleCancel,
      onClick,
      isShowCancelButton = true,
    }) => ({
      progressBarId: id,
      show: showCondition,
      title,
      percent,
      variantLinearProgress: variant,
      color,
      importInfo,
      cancelImportType,
      handleCancel,
      onClickProgressBar: onClick,
      isShowCancelButton,
    })

    const backgroundProgressBars = [
      createProgressBar({
        id: 'CSVImport',
        showCondition: !(isPathnameAllowed || !showProgressBarOnCSVImport),
        title:
          activeStep === STEP_PROGRESS_TWO
            ? 'subscribers.import.progressBar.text9'
            : 'subscribers.import.progressBar.text10',
        percent,
        variant: 'determinate',
        color: percent === 100 ? 'primary' : 'secondary',
        importInfo: fileName ? (
          <>
            <Box ml="4px" component="span" maxWidth="70%" className="truncate">
              {fileName}
            </Box>
            <Box
              ml="2px"
              component="span"
              color={themesConfig.default.colors['grey-400']}
            >
              {`(${fileSize})`}
            </Box>
          </>
        ) : null,
        cancelImportType: CANCEL_IMPORT_CSV,
        handleCancel: cancelUploadOrImport,
      }),
      createProgressBar({
        id: 'SISImport',
        showCondition: !(isPathnameAllowed || !showProgressBarOnSISImport),
        title: isCancellationProcess
          ? 'subscribers.import.progressBar.text8'
          : isStartedSISImport
            ? 'subscribers.import.progressBar.text5'
            : 'subscribers.import.progressBar.text7',
        percent: isCompletedSISImport ? 100 : 40,
        variant: isCompletedSISImport ? 'determinate' : 'indeterminate',
        color: isCompletedSISImport ? 'primary' : 'secondary',
        importInfo: isCancellationProcess ? null : (
          <Box ml="4px" component="span" maxWidth="70%" className="truncate">
            <FormattedMessage
              id={
                isCompletedSISImport
                  ? 'subscribers.import.progressBar.text7'
                  : 'subscribers.import.progressBar.text6'
              }
            />
          </Box>
        ),
        cancelImportType: CANCEL_SIS_IMPORT,
        handleCancel: cancelUploadOrImport,
        onClick: goToImportSubscriberPage,
        isShowCancelButton: !isCancellationProcess,
      }),
    ]

    return backgroundProgressBars.filter(({ show }) => show)
  }, [
    activeStep,
    status,
    fileName,
    fileSize,
    percent,
    SISImportStatus,
    showProgressBarOnCSVImport,
    showProgressBarOnSISImport,
    pathname,
  ])

  return (
    <>
      {!!activeBackgroundProgressBars.length && (
        <div className={classes.progressBarWrap}>
          {activeBackgroundProgressBars.map((props, index) => {
            return (
              <ProgressBar {...props} index={index} key={props.progressBarId} />
            )
          })}
        </div>
      )}
      <CancelImportDialog />
    </>
  )
}

export default memo(BackgroundProgressBarImportProcess)
